import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import "./NewsPage.css";
import NewsPageLoader from "./NewsPageLoader";
import { FaNewspaper } from "react-icons/fa";
import { FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } from "react-icons/fi";
import {useTranslation} from "react-i18next";

const removeHtmlAndEntities = (text) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = text;
    return tempDiv.textContent || tempDiv.innerText || "";
};

const truncateText = (text, limit) => {
    const cleanText = removeHtmlAndEntities(text);
    return cleanText.length > limit ? cleanText.substring(0, limit) + "..." : cleanText;
};

const NewsPage = () => {
    const { page } = useParams();
    const [news, setNews] = useState([]);
    const [currentPage, setCurrentPage] = useState(Number(page) || 1);
    const [loading, setLoading] = useState(true);
    const itemsPerPage = 5;
    const navigate = useNavigate();
    const {t, i18n} = useTranslation();

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const dateOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
        const timeOptions = { hour: '2-digit', minute: '2-digit', hour12: false }; // 24-hour format

        const formattedDate = date.toLocaleDateString('en-GB', dateOptions);
        const formattedTime = date.toLocaleTimeString('en-GB', timeOptions);

        return `${formattedDate}, ${formattedTime}`; // Combines date and time
    };

    useEffect(() => {
        axios
            .get(`${process.env.REACT_APP_API_BASE_URL}/GetAllNews`)
            .then((response) => {
                if (response.data.success) {
                    const filtered = response.data.data.filter(news => !news.isTemplate).reverse();
                    setNews(filtered);
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);


    useEffect(() => {
        if (page && Number(page) !== currentPage) {
            setCurrentPage(Number(page));
            window.scrollTo(0, 0);
        }
    }, [page]);

    const paginatedNews = news.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handlePageChange = (p) => {
        setCurrentPage(p);
        navigate(`/news/${p}`);
        window.scrollTo(0, 0);
    };

    return (
        <div className="news-page-background">
            <div className="news-page">
                <h1 className="page-title">
                    <FaNewspaper className="news-icon"/>
                    {t('სიახლეები')}
                </h1>
                {loading ? (
                    <NewsPageLoader/>
                ) : (
                    <>
                        <div className="news-list">
                            {paginatedNews.map((item) => (
                                <div
                                    onClick={() => {
                                        navigate("/new/" + item.id.toString());
                                    }}
                                    className="news-card-vertical" key={item.id}
                                >
                                    {item.path !== null && (
                                        <img
                                            src={item.path.trim()}
                                            alt={item.titleEng || item.title}
                                            className="news-image-vertical"
                                        />

                                    )}


                                    <div className="news-content-vertical">
                                        <p className="news-description">{formatDate(item.cereateDate)}</p>
                                        <div>
                                            {i18n.language === 'geo' ? (
                                                <div className="geo-title">
                                                    {item.title}
                                                </div>
                                            ) : (
                                                <div className="eng-title">
                                                    {item.titleEng}
                                                </div>
                                            )}
                                        </div>
                                        {i18n.language === 'geo' ? (
                                            <p className="news-description">
                                                {truncateText(item.descriptionShort ? item.descriptionShort : item.description, 250)}
                                            </p>
                                        ) : (
                                            <p className="news-description">
                                                {truncateText(item.descriptionShortEng ? item.descriptionShortEng : item.descriptionEng, 250)}
                                            </p>
                                        )}

                                        {i18n.language === 'geo' ? (
                                            <button
                                                className="navigate-button"
                                            >
                                                {item.buttonText ? item.buttonText : t('გაიგე მეტი')}
                                            </button>
                                        ) : (
                                            <button
                                                className="navigate-button"
                                            >
                                                {item.buttonTextEng ? item.buttonTextEng : t('გაიგე მეტი')}
                                            </button>
                                        )}

                                    </div>
                                </div>
                            ))}
                        </div>
                        <Pagination
                            totalItems={news.length}
                            itemsPerPage={itemsPerPage}
                            currentPage={currentPage}
                            onPageChange={handlePageChange}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

const Pagination = ({ totalItems, itemsPerPage, currentPage, onPageChange }) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    let startPage = currentPage - 2;
    let endPage = currentPage + 2;

    if (startPage <= 1) {
        startPage = 1;
        endPage = Math.min(5, totalPages);
    }
    if (endPage >= totalPages) {
        endPage = totalPages;
        startPage = Math.max(totalPages - 4, 1);
    }

    for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
    }

    const changePage = (newPage) => {
        if (newPage === currentPage) return;
        onPageChange(newPage);
    };

    return (
        <div className="pagination">
            <button className="page-button" onClick={() => changePage(1)} disabled={currentPage === 1}>
                <FiChevronsLeft />
            </button>
            <button className="page-button" onClick={() => changePage(currentPage - 1)} disabled={currentPage === 1}>
                <FiChevronLeft />
            </button>
            {startPage > 1 && <button className="page-button" onClick={() => changePage(1)}>1</button>}
            {startPage > 2 && <span className="ellipsis">...</span>}
            {pageNumbers.map(number => (
                <button
                    key={number}
                    className={`page-button ${currentPage === number ? "active" : ""}`}
                    onClick={() => changePage(number)}
                >
                    {number}
                </button>
            ))}
            {endPage < totalPages - 1 && <span className="ellipsis">...</span>}
            {endPage < totalPages && <button className="page-button" onClick={() => changePage(totalPages)}>{totalPages}</button>}
            <button className="page-button" onClick={() => changePage(currentPage + 1)} disabled={currentPage === totalPages}>
                <FiChevronRight />
            </button>
            <button className="page-button" onClick={() => changePage(totalPages)} disabled={currentPage === totalPages}>
                <FiChevronsRight />
            </button>
        </div>
    );
};

export default NewsPage;
